import React, { memo, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import PilotageStructuresApi from '../api/PilotageStructuresApi';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    borderRadius: '20px',
    background: 'radial-gradient(circle at 5% 0%, #d9f3ff 0, transparent 35%), linear-gradient(145deg, #f5f9f7 0%, #edf5ff 100%)',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  shell: {
    maxWidth: '1240px',
    margin: '0 auto',
  },
  hero: {
    padding: theme.spacing(3),
    borderRadius: '20px',
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(120deg, #0b2441 0%, #124560 48%, #0f766e 100%)',
    color: '#eaf7ff',
    boxShadow: '0 18px 40px rgba(12, 38, 58, 0.25)',
  },
  eyebrow: {
    fontSize: '0.78rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontWeight: 800,
    color: 'rgba(224, 242, 254, 0.95)',
  },
  heroTitle: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.7),
    fontWeight: 800,
    color: '#ffffff',
  },
  heroSubtitle: {
    margin: 0,
    maxWidth: '740px',
    color: 'rgba(230, 248, 255, 0.94)',
  },
  card: {
    height: '100%',
    borderRadius: '18px',
    padding: theme.spacing(2.2),
    background: 'rgba(255,255,255,0.94)',
    border: '1px solid rgba(16, 66, 52, 0.1)',
    boxShadow: '0 16px 34px rgba(16, 66, 52, 0.12)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  cardTitle: {
    fontWeight: 700,
    color: '#143a32',
  },
  field: {
    marginBottom: theme.spacing(1.2),
    '& .MuiInputBase-root': {
      borderRadius: '12px',
      background: '#ffffff',
    },
  },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.2),
    flexWrap: 'wrap',
  },
  primaryBtn: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '999px',
    background: 'linear-gradient(90deg, #0f766e 0%, #0f9a6d 100%)',
  },
  secondaryBtn: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '999px',
  },
  tableWrap: {
    borderRadius: '12px',
    border: '1px solid rgba(16, 66, 52, 0.12)',
    overflow: 'hidden',
    marginTop: theme.spacing(1),
  },
  headCell: {
    fontWeight: 700,
    background: '#f1fbf7',
    color: '#143a32',
  },
  rowActions: {
    display: 'flex',
    gap: theme.spacing(0.8),
    justifyContent: 'flex-end',
  },
  smallBtn: {
    textTransform: 'none',
    fontWeight: 700,
    minWidth: 'auto',
    padding: theme.spacing(0.45, 1),
  },
  error: {
    color: '#b91c1c',
    marginTop: theme.spacing(1),
    fontSize: '0.88rem',
  },
}));

const dedupliquerParId = (elements = []) => {
  const source = Array.isArray(elements) ? elements : [];
  const vus = new Set();

  return source.filter((element, index) => {
    const id = String(element?.id || '');
    const nom = String(element?.nom || '');
    const cle = id ? `id:${id}` : `fallback:${nom}:${index}`;

    if (vus.has(cle)) {
      return false;
    }

    vus.add(cle);
    return true;
  });
};

const cleElement = (prefixe, element, index) => `${prefixe}-${String(element?.id || 'sans-id')}-${index}`;

const PilotageStructuresSanitaireContainer = memo(() => {
  const classes = useStyles();
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [formations, setFormations] = useState([]);

  const [regionSelectionnee, setRegionSelectionnee] = useState('');
  const [districtSelectionne, setDistrictSelectionne] = useState('');

  const [nomRegion, setNomRegion] = useState('');
  const [idRegionEdition, setIdRegionEdition] = useState('');

  const [nomDistrict, setNomDistrict] = useState('');
  const [idDistrictEdition, setIdDistrictEdition] = useState('');

  const [nomFormation, setNomFormation] = useState('');
  const [idFormationEdition, setIdFormationEdition] = useState('');

  const [erreur, setErreur] = useState('');

  const chargerRegions = async () => {
    const data = await PilotageStructuresApi.obtenirRegions();
    setRegions(dedupliquerParId(data));
  };

  const chargerDistricts = async (regionId) => {
    if (!regionId) {
      setDistricts([]);
      return;
    }

    const data = await PilotageStructuresApi.obtenirDistricts(regionId);
    setDistricts(dedupliquerParId(data));
  };

  const chargerFormations = async (districtId) => {
    if (!districtId) {
      setFormations([]);
      return;
    }

    const data = await PilotageStructuresApi.obtenirFormationsSanitaires(districtId);
    setFormations(dedupliquerParId(data));
  };

  useEffect(() => {
    const initialiser = async () => {
      try {
        await chargerRegions();
      } catch (error) {
        setErreur('Impossible de charger les structures sanitaires.');
      }
    };

    initialiser();
  }, []);

  useEffect(() => {
    const syncDistricts = async () => {
      setDistrictSelectionne('');
      setFormations([]);
      try {
        await chargerDistricts(regionSelectionnee);
      } catch (error) {
        setErreur('Impossible de charger les districts de la region.');
      }
    };

    syncDistricts();
  }, [regionSelectionnee]);

  useEffect(() => {
    const syncFormations = async () => {
      try {
        await chargerFormations(districtSelectionne);
      } catch (error) {
        setErreur('Impossible de charger les formations sanitaires du district.');
      }
    };

    syncFormations();
  }, [districtSelectionne]);

  const resetRegionForm = () => {
    setNomRegion('');
    setIdRegionEdition('');
  };

  const resetDistrictForm = () => {
    setNomDistrict('');
    setIdDistrictEdition('');
  };

  const resetFormationForm = () => {
    setNomFormation('');
    setIdFormationEdition('');
  };

  const soumettreRegion = async () => {
    if (!nomRegion.trim()) return;

    try {
      setErreur('');
      if (idRegionEdition) {
        await PilotageStructuresApi.modifierRegion(idRegionEdition, nomRegion.trim());
      } else {
        await PilotageStructuresApi.ajouterRegion(nomRegion.trim());
      }
      await chargerRegions();
      resetRegionForm();
    } catch (error) {
      setErreur('Echec de l enregistrement de la region.');
    }
  };

  const supprimerRegion = async (id) => {
    try {
      setErreur('');
      await PilotageStructuresApi.supprimerRegion(id);
      if (regionSelectionnee === id) {
        setRegionSelectionnee('');
      }
      await chargerRegions();
    } catch (error) {
      setErreur('Echec de suppression de la region.');
    }
  };

  const soumettreDistrict = async () => {
    if (!nomDistrict.trim() || !regionSelectionnee) return;

    try {
      setErreur('');
      if (idDistrictEdition) {
        await PilotageStructuresApi.modifierDistrict(idDistrictEdition, nomDistrict.trim(), regionSelectionnee);
      } else {
        await PilotageStructuresApi.ajouterDistrict(nomDistrict.trim(), regionSelectionnee);
      }
      await chargerDistricts(regionSelectionnee);
      resetDistrictForm();
    } catch (error) {
      setErreur('Echec de l enregistrement du district.');
    }
  };

  const supprimerDistrict = async (id) => {
    try {
      setErreur('');
      await PilotageStructuresApi.supprimerDistrict(id);
      if (districtSelectionne === id) {
        setDistrictSelectionne('');
      }
      await chargerDistricts(regionSelectionnee);
    } catch (error) {
      setErreur('Echec de suppression du district.');
    }
  };

  const soumettreFormation = async () => {
    if (!nomFormation.trim() || !districtSelectionne) return;

    try {
      setErreur('');
      if (idFormationEdition) {
        await PilotageStructuresApi.modifierFormationSanitaire(idFormationEdition, nomFormation.trim(), districtSelectionne);
      } else {
        await PilotageStructuresApi.ajouterFormationSanitaire(nomFormation.trim(), districtSelectionne);
      }
      await chargerFormations(districtSelectionne);
      resetFormationForm();
    } catch (error) {
      setErreur('Echec de l enregistrement de la formation sanitaire.');
    }
  };

  const supprimerFormation = async (id) => {
    try {
      setErreur('');
      await PilotageStructuresApi.supprimerFormationSanitaire(id);
      await chargerFormations(districtSelectionne);
    } catch (error) {
      setErreur('Echec de suppression de la formation sanitaire.');
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.shell}>
        <Paper className={classes.hero} elevation={0}>
          <Typography className={classes.eyebrow}>Pilotage</Typography>
          <Typography variant="h4" className={classes.heroTitle}>Pilotage des structures sanitaires</Typography>
          <Typography className={classes.heroSubtitle}>
            Gere les Regions, Districts et Formations sanitaires depuis une seule interface moderne. Commence par choisir une region puis un district pour activer les sections dependantes.
          </Typography>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={4}>
            <Paper className={classes.card} elevation={0}>
              <div className={classes.cardHeader}>
                <Typography className={classes.cardTitle}>Regions sanitaires</Typography>
                <Chip size="small" color="primary" label={`${regions.length}`} />
              </div>
              <Divider />

              <TextField
                className={classes.field}
                label="Nom de la region"
                value={nomRegion}
                onChange={(e) => setNomRegion(e.target.value)}
                fullWidth
                margin="normal"
              />
              <div className={classes.actionRow}>
                <Button className={classes.primaryBtn} variant="contained" onClick={soumettreRegion}>
                  {idRegionEdition ? 'Modifier' : 'Ajouter'}
                </Button>
                <Button className={classes.secondaryBtn} variant="outlined" onClick={resetRegionForm}>
                  Reinitialiser
                </Button>
              </div>

              <TableContainer className={classes.tableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headCell}>Nom</TableCell>
                      <TableCell className={classes.headCell} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {regions.map((region) => (
                      <TableRow
                        key={region.id}
                        hover
                        selected={regionSelectionnee === region.id}
                        onClick={() => setRegionSelectionnee(region.id)}
                      >
                        <TableCell>{region.nom}</TableCell>
                        <TableCell align="right">
                          <div className={classes.rowActions}>
                            <Button className={classes.smallBtn} size="small" onClick={() => {
                              setIdRegionEdition(region.id);
                              setNomRegion(region.nom);
                            }}>Editer</Button>
                            <Button className={classes.smallBtn} size="small" color="secondary" onClick={() => supprimerRegion(region.id)}>Supprimer</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper className={classes.card} elevation={0}>
              <div className={classes.cardHeader}>
                <Typography className={classes.cardTitle}>Districts sanitaires</Typography>
                <Chip size="small" color="primary" label={`${districts.length}`} />
              </div>
              <Divider />

              <TextField
                className={classes.field}
                select
                label="Region parent"
                value={regionSelectionnee}
                onChange={(e) => setRegionSelectionnee(e.target.value)}
                fullWidth
                margin="normal"
              >
                <MenuItem value="">Choisir une region</MenuItem>
                {regions.map((region, index) => (
                  <MenuItem key={cleElement('region-option', region, index)} value={region.id}>{region.nom}</MenuItem>
                ))}
              </TextField>
              <TextField
                className={classes.field}
                label="Nom du district"
                value={nomDistrict}
                onChange={(e) => setNomDistrict(e.target.value)}
                fullWidth
                margin="normal"
                disabled={!regionSelectionnee}
              />
              <div className={classes.actionRow}>
                <Button className={classes.primaryBtn} variant="contained" onClick={soumettreDistrict} disabled={!regionSelectionnee}>
                  {idDistrictEdition ? 'Modifier' : 'Ajouter'}
                </Button>
                <Button className={classes.secondaryBtn} variant="outlined" onClick={resetDistrictForm}>
                  Reinitialiser
                </Button>
              </div>

              <TableContainer className={classes.tableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headCell}>Nom</TableCell>
                      <TableCell className={classes.headCell} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {districts.map((district, index) => (
                      <TableRow
                        key={cleElement('district-row', district, index)}
                        hover
                        selected={districtSelectionne === district.id}
                        onClick={() => setDistrictSelectionne(district.id)}
                      >
                        <TableCell>{district.nom}</TableCell>
                        <TableCell align="right">
                          <div className={classes.rowActions}>
                            <Button className={classes.smallBtn} size="small" onClick={() => {
                              setIdDistrictEdition(district.id);
                              setNomDistrict(district.nom);
                            }}>Editer</Button>
                            <Button className={classes.smallBtn} size="small" color="secondary" onClick={() => supprimerDistrict(district.id)}>Supprimer</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper className={classes.card} elevation={0}>
              <div className={classes.cardHeader}>
                <Typography className={classes.cardTitle}>Formations sanitaires</Typography>
                <Chip size="small" color="primary" label={`${formations.length}`} />
              </div>
              <Divider />

              <TextField
                className={classes.field}
                select
                label="District parent"
                value={districtSelectionne}
                onChange={(e) => setDistrictSelectionne(e.target.value)}
                fullWidth
                margin="normal"
                disabled={!regionSelectionnee}
              >
                <MenuItem value="">Choisir un district</MenuItem>
                {districts.map((district, index) => (
                  <MenuItem key={cleElement('district-option', district, index)} value={district.id}>{district.nom}</MenuItem>
                ))}
              </TextField>
              <TextField
                className={classes.field}
                label="Nom de la formation sanitaire"
                value={nomFormation}
                onChange={(e) => setNomFormation(e.target.value)}
                fullWidth
                margin="normal"
                disabled={!districtSelectionne}
              />
              <div className={classes.actionRow}>
                <Button className={classes.primaryBtn} variant="contained" onClick={soumettreFormation} disabled={!districtSelectionne}>
                  {idFormationEdition ? 'Modifier' : 'Ajouter'}
                </Button>
                <Button className={classes.secondaryBtn} variant="outlined" onClick={resetFormationForm}>
                  Reinitialiser
                </Button>
              </div>

              <TableContainer className={classes.tableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headCell}>Nom</TableCell>
                      <TableCell className={classes.headCell} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formations.map((formation, index) => (
                      <TableRow key={cleElement('formation-row', formation, index)} hover>
                        <TableCell>{formation.nom}</TableCell>
                        <TableCell align="right">
                          <div className={classes.rowActions}>
                            <Button className={classes.smallBtn} size="small" onClick={() => {
                              setIdFormationEdition(formation.id);
                              setNomFormation(formation.nom);
                            }}>Editer</Button>
                            <Button className={classes.smallBtn} size="small" color="secondary" onClick={() => supprimerFormation(formation.id)}>Supprimer</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {erreur && <Typography className={classes.error}>{erreur}</Typography>}
      </div>
    </div>
  );
});

export default PilotageStructuresSanitaireContainer;